import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";
import { useState, useEffect } from "react";
// Country list for the Country dropdown field. ISO 3166-1 alpha-2, with
// flag emoji and dial code for phone input. Trimmed to the global set.
export const COUNTRIES = [
  { code: "AU", name: "Australia", dial: "+61", flag: "🇦🇺" },
  { code: "NZ", name: "New Zealand", dial: "+64", flag: "🇳🇿" },
  { code: "US", name: "United States", dial: "+1", flag: "🇺🇸" },
  { code: "CA", name: "Canada", dial: "+1", flag: "🇨🇦" },
  { code: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧" },
  { code: "PL", name: "Poland", dial: "+48", flag: "🇵🇱" },
  { code: "IE", name: "Ireland", dial: "+353", flag: "🇮🇪" },
  { code: "SG", name: "Singapore", dial: "+65", flag: "🇸🇬" },
  { code: "HK", name: "Hong Kong", dial: "+852", flag: "🇭🇰" },
  { code: "AE", name: "United Arab Emirates", dial: "+971", flag: "🇦🇪" },
  { code: "IN", name: "India", dial: "+91", flag: "🇮🇳" },
  { code: "PH", name: "Philippines", dial: "+63", flag: "🇵🇭" },
  { code: "MY", name: "Malaysia", dial: "+60", flag: "🇲🇾" },
  { code: "ID", name: "Indonesia", dial: "+62", flag: "🇮🇩" },
  { code: "TH", name: "Thailand", dial: "+66", flag: "🇹🇭" },
  { code: "VN", name: "Vietnam", dial: "+84", flag: "🇻🇳" },
  { code: "JP", name: "Japan", dial: "+81", flag: "🇯🇵" },
  { code: "KR", name: "South Korea", dial: "+82", flag: "🇰🇷" },
  { code: "CN", name: "China", dial: "+86", flag: "🇨🇳" },
  { code: "DE", name: "Germany", dial: "+49", flag: "🇩🇪" },
  { code: "FR", name: "France", dial: "+33", flag: "🇫🇷" },
  { code: "ES", name: "Spain", dial: "+34", flag: "🇪🇸" },
  { code: "IT", name: "Italy", dial: "+39", flag: "🇮🇹" },
  { code: "NL", name: "Netherlands", dial: "+31", flag: "🇳🇱" },
  { code: "SE", name: "Sweden", dial: "+46", flag: "🇸🇪" },
  { code: "NO", name: "Norway", dial: "+47", flag: "🇳🇴" },
  { code: "DK", name: "Denmark", dial: "+45", flag: "🇩🇰" },
  { code: "FI", name: "Finland", dial: "+358", flag: "🇫🇮" },
  { code: "ZA", name: "South Africa", dial: "+27", flag: "🇿🇦" },
  { code: "BR", name: "Brazil", dial: "+55", flag: "🇧🇷" },
  { code: "MX", name: "Mexico", dial: "+52", flag: "🇲🇽" },
  { code: "AR", name: "Argentina", dial: "+54", flag: "🇦🇷" },
];

export const POPULAR_COUNTRIES = ["US","CA","GB","AU","NZ","PL"];
export function popularFirst(rows) {
  return [...rows].sort((a,b)=>{
    const ai=POPULAR_COUNTRIES.indexOf(a.code),bi=POPULAR_COUNTRIES.indexOf(b.code);
    return (ai<0?999:ai)-(bi<0?999:bi) || a.name.localeCompare(b.name);
  });
}
export const DEFAULT_COUNTRY = "AU";

export function dialFor(code) {
  const c = COUNTRIES.find((x) => x.code === (code || DEFAULT_COUNTRY));
  return c ? c.dial : "+61";
}

export function formatPhone(raw, countryCode, opts) {
  let digits = (countryCode === "AU" || !countryCode) ? auPhoneDigits(raw) : String(raw || "").replace(/[^\d]/g, "");
  if (!digits) return "";
  if(countryCode==="AU" || !countryCode)digits=digits.slice(0,/^13/.test(digits)&&!/^1300/.test(digits)?6:10);
  const forcedKind = opts && opts.kind;    // "mobile" | "landline" | undefined
  if (countryCode === "AU" || !countryCode) {
    // AU numbers come in two shapes depending on the first two digits:
    //  • Mobile  — starts with 04 → 04XX XXX XXX (4-3-3)
    //  • Landline — starts with 02/03/07/08 → 0X XXXX XXXX (2-4-4)
    // When the owning field is pinned to one kind (mobile_phone /
    // landline_phone), ALWAYS use that kind's layout — even if the user's
    // digits don't match the expected prefix — so the input shape never
    // mutates mid-type. The validator shows the error separately.
    if (/^(1300|1800)/.test(digits)) return [digits.slice(0,4),digits.slice(4,7),digits.slice(7)].filter(Boolean).join(" ");
    if (/^13/.test(digits)) return [digits.slice(0,2),digits.slice(2,4),digits.slice(4)].filter(Boolean).join(" ");
    const prefix = digits.slice(0, 2);
    const isMobile   = forcedKind === "mobile"   || (!forcedKind && prefix === "04");
    const isLandline = forcedKind === "landline" || (!forcedKind && ["02", "03", "07", "08"].includes(prefix));
    if (isMobile) {
      if (digits.length <= 4) return digits;
      if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    if (isLandline) {
      if (digits.length <= 2) return digits;
      if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
      return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
    }
    return digits;
  }
  return new AsYouType(countryCode || DEFAULT_COUNTRY).input(digits);

}


// AU validation shared by all form phone fields; independent of network availability.
export function auPhoneDigits(raw) {
  let text=String(raw || "").trim(), d=text.replace(/[^0-9]/g,"");
  if(text.startsWith("+61") || text.startsWith("0061")) d=d.slice(text.startsWith("0061")?4:2);
  else if(d.startsWith("61") && [11,12].includes(d.length)) d=d.slice(2);
  if(/^[23478]/.test(d) && d.length===9)d="0"+d;
  return d;
}
export function validateAuPhone(raw, {kind="any",state=""}={}) {
  const d=auPhoneDigits(raw);
  if(!d)return {ok:true,kind:"empty",message:""};
  const mobile=/^04[0-9]{8}$/.test(d), geographic=/^0[2378][0-9]{8}$/.test(d);
  const service=/^(?:13[0-9]{4}|(?:1300|1800)[0-9]{6})$/.test(d);
  let message="";
  if(kind==="mobile") message=!d.startsWith("04")?"Mobile number must begin with 04.":!mobile?"Mobile number must contain 10 digits.":"";
  else if(kind==="landline") message=!(geographic||service)?"Enter a landline beginning with 02, 03, 07 or 08 (10 digits), or a valid 13 (6 digits), 1300 or 1800 number (10 digits).":"";
  else if(!(mobile||geographic||service))message="Enter a valid Australian mobile, landline, 13, 1300 or 1800 number.";
  const areas={ACT:"02",NSW:"02",VIC:"03",TAS:"03",QLD:"07",SA:"08",WA:"08",NT:"08"};
  if(!message && geographic && state && areas[state.toUpperCase()]!==d.slice(0,2))message="This landline must start with "+(areas[state.toUpperCase()]||"a valid state area code")+".";
  return {ok:!message,message,kind:mobile?"mobile":geographic?"landline":service?"service":"invalid"};
}


let countryRequest=null;
export async function loadCountries() {
  if(!countryRequest)countryRequest=(async()=>{
    const base=process.env.REACT_APP_BACKEND_URL || "";
    const response=await fetch(base+"/api/geo/countries",{credentials:"omit"});
    if(!response.ok)throw new Error("Country reference unavailable");
    const data=await response.json();
    if(!Array.isArray(data.items)||data.items.length<200)throw new Error("Country reference incomplete");
    const rows=data.items.map(row=>({code:row.iso2,name:row.country_name,
      dial:row.calling_codes?.[0] || "",callingCodes:row.calling_codes || [],
      flag:row.flag,countryKey:row.country_key,iso3:row.iso3}));
    COUNTRIES.splice(0,COUNTRIES.length,...rows);
    return rows;
  })().catch(error=>{countryRequest=null;throw error;});
  return countryRequest;
}
export function useCountries() {
  const [rows,setRows]=useState(()=>popularFirst(COUNTRIES));
  useEffect(()=>{let mounted=true;loadCountries().then(data=>{if(mounted)setRows(popularFirst(data));}).catch(()=>{ /* retain safe offline fallback */ });return()=>{mounted=false;};},[]);
  return rows;
}
export async function loadCountryRegions(code,parent) {
  const base=process.env.REACT_APP_BACKEND_URL || "";
  const suffix=parent===undefined?"":"?parent="+encodeURIComponent(parent);
  const response=await fetch(base+"/api/geo/countries/"+encodeURIComponent(code)+"/regions"+suffix,{credentials:"omit"});
  if(!response.ok)throw new Error("Could not load country regions");
  return (await response.json()).items || [];
}


// Shared US city/postal lookup for both city suggestions and the ZIP field.
let usRegionsRequest=null;
const cityPostalRequests=new Map();
export async function loadCityPostcodes(country,city,region) {
  if(country!=="US" || !city || !region)return [];
  const key=[country,city.trim().toLowerCase(),region.trim().toLowerCase()].join("|");
  const cached=cityPostalRequests.get(key);
  if(cached && Date.now()-cached.at<3600000)return cached.promise;
  const promise=(async()=>{
    if(!usRegionsRequest)usRegionsRequest=loadCountryRegions("US").catch(error=>{usRegionsRequest=null;throw error;});
    const regions=await usRegionsRequest;
    const match=regions.find(x=>x.name.toLowerCase()===region.trim().toLowerCase() || x.iso3166_2==="US-"+region.trim().toUpperCase());
    const state=match?.iso3166_2?.split("-")[1] || (/^[a-z]{2}$/i.test(region.trim())?region.trim():"");
    if(!state)return [];
    const response=await fetch("https://api.zippopotam.us/us/"+encodeURIComponent(state)+"/"+encodeURIComponent(city.trim()));
    if(!response.ok)throw new Error("Postal lookup unavailable");
    const data=await response.json();
    return [...new Set((data.places||[]).map(x=>x["post code"]).filter(Boolean))].sort();
  })().catch(()=>{cityPostalRequests.delete(key);return [];});
  if(cityPostalRequests.size>=200)cityPostalRequests.delete(cityPostalRequests.keys().next().value);
  cityPostalRequests.set(key,{promise,at:Date.now()});
  return promise;
}

export async function loadPostalPlaces(country,postcode) {
  if(country!=="US" || !/^\d{5}$/.test(String(postcode||"").trim()))return [];
  const response=await fetch("https://api.zippopotam.us/us/"+encodeURIComponent(postcode.trim()));
  if(response.status===404)return [];
  if(!response.ok)throw new Error("Postal lookup unavailable");
  const data=await response.json();
  return (data.places||[]).map(x=>({city:x["place name"],region:x.state,postcode:postcode.trim()})).filter(x=>x.city);
}

// Browser locale is a preference, not verified physical location.
export function browserCountry(fallback=DEFAULT_COUNTRY, locales=typeof navigator!=='undefined'?(navigator.languages?.length?navigator.languages:[navigator.language]):[]) {
 for(const locale of locales || []) {
  try { const region=new Intl.Locale(locale).region; if(region && COUNTRIES.some(c=>c.code===region))return region; }catch{}
 }
 return fallback;
}
export function defaultPhoneCountry(value,fallback=DEFAULT_COUNTRY) {
 const preferred=browserCountry(fallback);
 try { if(String(value||'').trim().startsWith('+'))return parsePhoneNumberFromString(String(value))?.country || preferred; }catch{}
 return preferred;
}
